/**
 * A JS Self-Profiling API sampler that starts before the app renders, so a reload's first
 * mount is inside the trace. `new Profiler()` dies with its document, and a sampler started
 * from the console after the page is up misses everything before the list response, which
 * is the part of a reload that varies on the development host
 * (docs/working/2026-10-21-creatives-first-mount-profile.md). The document must carry
 * `Document-Policy: js-profiling` (frontend/nginx.conf); without it the constructor throws
 * and this does nothing.
 *
 * Off unless the page's localStorage holds `grovus.profile=1`. The running profiler is kept
 * at `window.grovusProfiler`; `await window.grovusProfiler.stop()` returns the trace.
 */
export const PROFILE_FLAG = 'grovus.profile'

interface ProfilerLike {
  stop(): Promise<unknown>
}

interface ProfilerConstructor {
  new (options: { sampleInterval: number; maxBufferSize: number }): ProfilerLike
}

declare global {
  interface Window {
    Profiler?: ProfilerConstructor
    grovusProfiler?: ProfilerLike
  }
}

export type ProfilerHost = Pick<Window, 'Profiler' | 'grovusProfiler'>

export function startProfilerIfRequested(host: ProfilerHost, storage: Pick<Storage, 'getItem'>): boolean {
  let requested = false
  try {
    requested = storage.getItem(PROFILE_FLAG) === '1'
  } catch {
    return false
  }
  if (!requested || typeof host.Profiler !== 'function') return false
  try {
    // Chrome samples no finer than about 10 ms whatever the interval asks for; the buffer
    // holds a few minutes of a busy page at that rate.
    host.grovusProfiler = new host.Profiler({ sampleInterval: 1, maxBufferSize: 200000 })
    return true
  } catch {
    return false
  }
}
